import { Dialog, Paragraph, XStack, YStack } from 'tamagui';
import type { Vehicle } from '../lib/api';
import { FocusRingButton } from './FocusRingButton';

// Minimum tap-target size (px) required on mobile for the dialog controls.
const MIN_TAP_TARGET_PX = 44;

/**
 * Confirmation dialog for permanently deleting a vehicle. Shared by the
 * Vehicles list and the VehicleDetail page so the copy and danger styling stay
 * identical. Open when `vehicle` is non-null.
 */
export function DeleteVehicleDialog({
  vehicle,
  onCancel,
  onConfirm,
  isPending,
}: {
  vehicle: Pick<Vehicle, 'year' | 'make' | 'model' | 'nickname'> | null;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog modal open={vehicle !== null} onOpenChange={(o) => (o ? undefined : onCancel())}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" opacity={0.5} />
        <Dialog.Content
          key="content"
          elevate
          padding="$4"
          width={440}
          maxWidth="90vw"
          gap="$3"
        >
          <Dialog.Title>Delete this vehicle?</Dialog.Title>
          {vehicle ? (
            <YStack gap="$2">
              <Paragraph>
                <strong>
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </strong>
                {vehicle.nickname ? ` (${vehicle.nickname})` : ''}
              </Paragraph>
              <Paragraph color="$danger">
                This permanently deletes the vehicle <strong>and all its service logs,
                maintenance schedule, mileage history, and notifications</strong>. This cannot
                be undone.
              </Paragraph>
              <Paragraph size="$2" color="$color10">
                If you're selling the car, export your records first (coming soon).
              </Paragraph>
            </YStack>
          ) : null}
          <XStack gap="$2" justifyContent="flex-end">
            <FocusRingButton
              minHeight={MIN_TAP_TARGET_PX}
              minWidth={MIN_TAP_TARGET_PX}
              onPress={onCancel}
              disabled={isPending}
            >
              Cancel
            </FocusRingButton>
            <FocusRingButton
              theme="red"
              minHeight={MIN_TAP_TARGET_PX}
              minWidth={MIN_TAP_TARGET_PX}
              onPress={onConfirm}
              disabled={isPending}
            >
              {isPending ? 'Deleting…' : 'Delete forever'}
            </FocusRingButton>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
